import { computed, onMounted, watch } from "vue";
import { useRoute } from "vue-router";

import { PRODUCT } from "@/constants/product.constants";
import { useContentStructureStore } from "@/stores/content-structure.store";
import { useProductsStore } from "@/stores/products.store";
import { useUIGeneralStore } from "@/stores/ui-general.store";

import {
  ContentStructureSection,
  GeneratedContentStructure
} from "@/models/content-structure.model";
import { ProductItem, ProductStatus } from "@/models/product.model";
import { TaskDisplay } from "@/models/task.model";

import { useTasksManager } from "@/composables/tasks-manager";

import { orderDisplaySectionByDeadline } from "@/utils/tasks-helpers";

export const useProductsManager = () => {
  const uiGeneralStore = useUIGeneralStore();
  const productsStore = useProductsStore();
  const contStrStore = useContentStructureStore();

  const route = useRoute();

  const isLoading = computed<boolean>(() => {
    return productsStore.areProductDetailsLoading;
  });
  const isUpdating = computed<boolean>(() => {
    return (
      productsStore.areProductDetailsUpdating ||
      contStrStore.isContentStructureUpdating
    );
  });

  const product = computed<ProductItem | undefined>(() => {
    if (isLoading.value || !route.params.productId) {
      return undefined;
    }
    return productsStore.productDetails?.find((e: ProductItem) => {
      return e.id === Number(route.params.productId);
    });
  });
  const structure = computed<GeneratedContentStructure | undefined>(() => {
    if (contStrStore.isContentStructureLoading) {
      return undefined;
    }
    return contStrStore.contentStructure.find(
      (e: GeneratedContentStructure) => {
        return e.product_id === Number(route.params.productId);
      }
    );
  });
  const rootSection = computed<ContentStructureSection | null>(() => {
    if (structure.value && structure.value.rootSection) {
      return structure.value.rootSection;
    }
    return null;
  });

  const isContentVisible = computed<boolean>(() => {
    return !isLoading.value && !!product.value;
  });

  onMounted(() => {
    if (product.value && product.value.name) {
      uiGeneralStore.setPageHeading(product.value.name);
    }
  });
  watch(
    () => product.value,
    newVal => {
      if (newVal && newVal.name) {
        uiGeneralStore.setPageHeading(newVal.name);
      }
    }
  );

  const { getTasksByProduct } = useTasksManager();
  const allTasksInAProduct = computed<TaskDisplay[]>(() => {
    if (!product.value) {
      return [];
    }
    const allTasks = getTasksByProduct(product.value.id);
    return allTasks.sort(orderDisplaySectionByDeadline);
  });

  const hasEditStructurePermission = computed<boolean>(() => {
    return (
      !!product.value &&
      product.value.allocated_permissions.some(
        e => e.id === PRODUCT.PERMISSIONS.editContentStructure
      )
    );
  });

  const isProductArchived = computed<boolean>(() => {
    return product.value?.status === ProductStatus.ARCHIVE;
  });

  return {
    isLoading,
    isUpdating,
    product,
    structure,
    rootSection,
    isContentVisible,
    allTasksInAProduct,
    hasEditStructurePermission,
    isProductArchived
  };
};
